import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookmarkPlus, CheckCircle2 } from 'lucide-react';
import { Button } from 'components/ui/button';

export const WORDS_PATH = '/portal/words';

interface DoneProps {
  total: number;
  learned: number;
  toRepeat: number;
  onRepeat: () => void;
}

/** "Mashq tugadi": learned vs to-repeat counts, a replay of the missed cards, and the way back. */
export function Done({ total, learned, toRepeat, onRepeat }: DoneProps) {
  const { t } = useTranslation();
  return (
    <section
      aria-labelledby="flashcards-done"
      className="mt-5 flex min-h-[420px] flex-col items-center justify-center rounded-3xl border border-border bg-card p-6 text-center shadow-[0_30px_60px_-36px_rgba(16,24,43,0.35)] md:rounded-[28px] md:p-9"
    >
      <p className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{t('flashcards.done_eyebrow')}</p>
      <h1 id="flashcards-done" className="mt-3 font-display text-[32px] font-extrabold leading-[1.1] tracking-[-0.045em] text-foreground md:text-[44px]">
        {t('flashcards.done_title', { learned, total })}
      </h1>
      <dl className="mt-6 flex gap-2.5 md:gap-3">
        <div className="rounded-2xl border border-border bg-card px-[18px] py-3.5 text-left">
          <dt className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{t('flashcards.stat_learned')}</dt>
          <dd className="font-display text-[28px] font-extrabold tracking-[-0.04em] text-[#166534] dark:text-emerald-300">{learned}</dd>
        </div>
        <div className="rounded-2xl border border-border bg-card px-[18px] py-3.5 text-left">
          <dt className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{t('flashcards.stat_repeat')}</dt>
          <dd className="font-display text-[28px] font-extrabold tracking-[-0.04em] text-[#B42318] dark:text-red-300">{toRepeat}</dd>
        </div>
      </dl>
      <div className="mt-7 flex w-full flex-col gap-2 md:w-auto md:flex-row md:gap-2.5">
        {toRepeat > 0 && (
          <Button type="button" onClick={onRepeat} className="h-12 bg-foreground px-[22px] text-background hover:bg-foreground/90">
            {t('flashcards.repeat_missed', { count: toRepeat })}
          </Button>
        )}
        <Button variant="outline" className="h-12 px-[22px]" asChild>
          <Link to={WORDS_PATH}>{t('flashcards.back_to_words')}</Link>
        </Button>
      </div>
    </section>
  );
}

interface EmptyDeckProps {
  /** The learner has words, but every one is already learned. */
  allLearned: boolean;
}

/** No cards to study: either no saved words yet, or all of them are learned. */
export function EmptyDeck({ allLearned }: EmptyDeckProps) {
  const { t } = useTranslation();
  const Icon = allLearned ? CheckCircle2 : BookmarkPlus;
  return (
    <section aria-labelledby="flashcards-empty" className="mt-5 flex min-h-[320px] flex-col items-center justify-center rounded-3xl border border-border bg-card px-6 py-12 text-center">
      <span aria-hidden className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <h1 id="flashcards-empty" className="font-display text-xl font-extrabold tracking-[-0.03em] text-foreground">
        {t(allLearned ? 'flashcards.all_learned_title' : 'flashcards.empty_title')}
      </h1>
      <p className="mt-1.5 max-w-[360px] text-sm text-muted-foreground">{t(allLearned ? 'flashcards.all_learned_body' : 'flashcards.empty_body')}</p>
      <Button variant="outline" className="mt-5 h-11" asChild>
        <Link to={WORDS_PATH}>{t('flashcards.back_to_words')}</Link>
      </Button>
    </section>
  );
}
